import { FC, useEffect, useState } from "react";
import {
  ItemGroup
} from "Types";


interface ProductGroupTableProps {
  itemGroups: ItemGroup[];
  onSelectItemGroup: (id: number | null) => void;
  onDeleteItemGroup: (id: number) => void;
  onViewItemGroup: (id: number) => void;
  onEditItemGroup: (id: number) => void;
  selectedItemGroup: number | null;
  isLoading?: boolean;
  isError?: boolean;
  selectedGroups?: number[];
  onSelectedGroupsChange?: (selectedIds: number[]) => void;
  itemsPerPage: number;
  totalItems: number;
  onItemsPerPageChange: (size: number) => void;
  onPageChange: (page: number) => void;
  currentPage: number;
}

export const ProductGroupTable: FC<ProductGroupTableProps> = ({
  itemGroups,
  onSelectItemGroup,
  onDeleteItemGroup,
  onViewItemGroup,
  onEditItemGroup,
  selectedItemGroup,
  isLoading = false,
  isError = false,
  selectedGroups: externalSelectedGroups = [],
  onSelectedGroupsChange,
  itemsPerPage,
  totalItems,
  onItemsPerPageChange,
  onPageChange,
  currentPage,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const triggerTransition = () => {
    setIsTransitioning(true);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  useEffect(() => {
    const currentPageGroupIds = itemGroups.map((group) => group.id);

    const allSelected =
      currentPageGroupIds.length > 0 &&
      currentPageGroupIds.every((id) => externalSelectedGroups.includes(id));

    setSelectAll(allSelected);
  }, [externalSelectedGroups, itemGroups]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const currentPageGroupIds = itemGroups.map((group) => group.id);

      onSelectedGroupsChange?.(currentPageGroupIds);

      if (currentPageGroupIds.length > 0) {
        onSelectItemGroup(currentPageGroupIds[0]);
      }
    } else {
      onSelectedGroupsChange?.([]);
      onSelectItemGroup(null);
    }
  };

  const handleSelectItemGroup = (id: number, checked: boolean) => {
    let newSelectedGroups: number[];

    if (checked) {
      newSelectedGroups = [...externalSelectedGroups, id];
    } else {
      newSelectedGroups = externalSelectedGroups.filter(
        (groupId: number) => groupId !== id,
      );
    }

    onSelectedGroupsChange?.(newSelectedGroups);

    if (newSelectedGroups.length > 0) {
      onSelectItemGroup(newSelectedGroups[0]);
    } else {
      onSelectItemGroup(null);
    }

    const currentPageGroupIds = itemGroups.map((group) => group.id);

    setSelectAll(
      currentPageGroupIds.every((id) => newSelectedGroups.includes(id)),
    );
  };

  // ... (table rendering remains the same until pagination)
  // Re-mapping the bottom pagination section
  return (
    <div className="font-body tracking-wider overflow-x-auto">
      {isError ? (
        <div className="py-10 text-center text-red-500">
          Error loading product groups
        </div>
      ) : (
        <>
          <div className="relative">
            {(isLoading || isTransitioning) && (
              <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center min-h-[200px]">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm font-medium text-gray-600">Loading...</span>
                </div>
              </div>
            )}
            <table className="min-w-full">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="py-3 px-4 text-left left-0 bg-white">
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                    Product Group Name
                  </th>
                  <th className="py-3 px-4 text-right text-sm font-semibold text-gray-500 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {itemGroups.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-6 text-center text-gray-500">
                      No product groups found
                    </td>
                  </tr>
                ) : (
                  itemGroups.map((group) => (
                    <tr
                      key={group.id}
                      className={`border-t ${
                        selectedItemGroup === group.id ? "bg-blue-50" : ""
                      }`}
                      onClick={() =>
                        onSelectItemGroup(
                          selectedItemGroup === group.id ? null : group.id,
                        )
                      }
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={externalSelectedGroups.includes(group.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectItemGroup(group.id, e.target.checked);
                          }}
                        />
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        {group.itemGroupName || ""}
                      </td>
                      <td className="py-3 px-4 flex gap-2 justify-end">
                        <button
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewItemGroup(group.id);
                          }}
                          title="View"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>

                        <button
                          className="text-green-500 hover:text-green-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditItemGroup(group.id);
                          }}
                          title="Edit"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </button>

                        <button
                          className="text-red-500 hover:text-red-700 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteItemGroup(group.id);
                          }}
                          title="Delete"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            <line x1="10" x2="10" y1="11" y2="17" />
                            <line x1="14" x2="14" y1="11" y2="17" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="hidden md:flex text-gray-600">
              Showing {startItem} to {endItem} of {totalItems} entries
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show</span>
                <select
                  className="p-2 border rounded bg-white hover:bg-gray-50 cursor-pointer outline-none focus:border-orange-400 transition-colors"
                  value={itemsPerPage}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    onItemsPerPageChange(newSize);
                    onPageChange(1);
                    triggerTransition();
                  }}
                >
                  {[100, 500, 1000, 1500].map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1">
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === 1 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(1);
                    triggerTransition();
                  }}
                  title="First Page"
                >
                  &lt;&lt;
                </button>
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === 1 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(currentPage - 1);
                    triggerTransition();
                  }}
                  title="Previous Page"
                >
                  &lt;
                </button>
                <div className="flex items-center justify-center min-w-[40px] h-9 bg-gray-50 border rounded-md font-medium text-orange-600">
                  {currentPage}
                </div>
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === totalPages || totalPages === 0 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(currentPage + 1);
                    triggerTransition();
                  }}
                  title="Next Page"
                >
                  &gt;
                </button>
                <button
                  className="px-3 py-1.5 border rounded-md hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                  disabled={currentPage === totalPages || totalPages === 0 || isLoading || isTransitioning}
                  onClick={() => {
                    onPageChange(totalPages);
                    triggerTransition();
                  }}
                  title="Last Page"
                >
                  &gt;&gt;
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
